import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  Timestamp,
  writeBatch,
  increment 
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { 
  Warranty, 
  WarrantyClaim, 
  CreateWarrantyData, 
  CreateWarrantyClaimData, 
  WarrantyQueryParams, 
  WarrantyClaimQueryParams,
  WarrantyStats,
  ClaimStatus 
} from '../types/warranty';

const COLLECTION_NAME = 'warranties';
const CLAIMS_COLLECTION_NAME = 'warranty_claims';

class WarrantyService {
  // Generate warranty number
  generateWarrantyNumber(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const time = now.getTime().toString().slice(-6);
    return `W-${year}${month}${day}-${time}`;
  }

  // Generate claim number
  generateClaimNumber(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const time = now.getTime().toString().slice(-6);
    return `C-${year}${month}${day}-${time}`;
  }

  // Create warranty
  async createWarranty(data: CreateWarrantyData, organizationId: string, userId: string): Promise<string> {
    try {
      const warrantyNumber = this.generateWarrantyNumber();
      const now = new Date();
      const endDate = new Date(data.startDate);
      endDate.setMonth(endDate.getMonth() + data.durationMonths);

      const warrantyData: Omit<Warranty, 'id'> = {
        warrantyNumber,
        productId: data.productId,
        productName: '', // Will be populated from product data
        productCode: '', // Will be populated from product data
        customerId: data.customerId,
        customerName: '', // Will be populated from customer data
        customerEmail: '', // Will be populated from customer data
        type: data.type,
        status: 'active',
        startDate: data.startDate,
        endDate,
        durationMonths: data.durationMonths,
        purchaseDate: data.purchaseDate,
        invoiceNumber: data.invoiceNumber,
        purchasePrice: data.purchasePrice,
        currency: data.currency,
        supplierId: data.supplierId,
        supplierName: '', // Will be populated from supplier data
        serialNumber: data.serialNumber,
        batchNumber: data.batchNumber,
        manufacturer: data.manufacturer,
        model: data.model,
        terms: data.terms,
        coverage: data.coverage,
        exclusions: data.exclusions || [],
        notes: data.notes,
        claimsCount: 0,
        organizationId,
        createdAt: now,
        updatedAt: now,
        createdBy: userId
      };

      const docRef = await addDoc(collection(db, COLLECTION_NAME), {
        ...warrantyData,
        startDate: Timestamp.fromDate(warrantyData.startDate),
        endDate: Timestamp.fromDate(warrantyData.endDate),
        purchaseDate: Timestamp.fromDate(warrantyData.purchaseDate),
        createdAt: Timestamp.fromDate(warrantyData.createdAt),
        updatedAt: Timestamp.fromDate(warrantyData.updatedAt)
      });

      return docRef.id;
    } catch (error) {
      console.error('Error creating warranty:', error);
      throw error;
    }
  }

  // Get warranties
  async getWarranties(organizationId: string, params: WarrantyQueryParams = {}): Promise<Warranty[]> {
    try {
      let q = query(
        collection(db, COLLECTION_NAME),
        where('organizationId', '==', organizationId),
        orderBy('createdAt', 'desc')
      );

      if (params.status) {
        q = query(q, where('status', '==', params.status));
      }

      if (params.type) {
        q = query(q, where('type', '==', params.type));
      }

      if (params.customerId) {
        q = query(q, where('customerId', '==', params.customerId));
      }

      if (params.productId) {
        q = query(q, where('productId', '==', params.productId));
      }

      const querySnapshot = await getDocs(q);
      let warranties = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        startDate: doc.data().startDate?.toDate() || new Date(),
        endDate: doc.data().endDate?.toDate() || new Date(),
        purchaseDate: doc.data().purchaseDate?.toDate() || new Date(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as Warranty[];

      // Client-side filtering for search term
      if (params.searchTerm) {
        const searchLower = params.searchTerm.toLowerCase();
        warranties = warranties.filter(w => 
          w.warrantyNumber.toLowerCase().includes(searchLower) ||
          w.productName.toLowerCase().includes(searchLower) ||
          w.customerName.toLowerCase().includes(searchLower) ||
          w.serialNumber?.toLowerCase().includes(searchLower)
        );
      }

      return warranties;
    } catch (error) {
      console.error('Error getting warranties:', error);
      throw error;
    }
  }

  // Update warranty
  async updateWarranty(id: string, data: Partial<Warranty>): Promise<void> {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      const updateData: any = {
        ...data,
        updatedAt: Timestamp.fromDate(new Date())
      };

      // Convert dates to Timestamps
      if (data.startDate) {
        updateData.startDate = Timestamp.fromDate(data.startDate);
      }
      if (data.endDate) {
        updateData.endDate = Timestamp.fromDate(data.endDate);
      }
      if (data.purchaseDate) {
        updateData.purchaseDate = Timestamp.fromDate(data.purchaseDate);
      }

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating warranty:', error);
      throw error;
    }
  }

  // Delete warranty
  async deleteWarranty(id: string): Promise<void> {
    try {
      const batch = writeBatch(db);
      
      // Delete warranty
      const warrantyRef = doc(db, COLLECTION_NAME, id);
      batch.delete(warrantyRef);

      // Delete all claims for this warranty
      const claimsQuery = query(
        collection(db, CLAIMS_COLLECTION_NAME),
        where('warrantyId', '==', id)
      );
      const claimsSnapshot = await getDocs(claimsQuery);
      
      claimsSnapshot.docs.forEach(claimDoc => {
        batch.delete(claimDoc.ref);
      });

      await batch.commit();
    } catch (error) {
      console.error('Error deleting warranty:', error);
      throw error;
    }
  }

  // Create warranty claim
  async createWarrantyClaim(data: CreateWarrantyClaimData, organizationId: string, userId: string): Promise<string> {
    try {
      const claimNumber = this.generateClaimNumber();
      const now = new Date();

      const claimData: Omit<WarrantyClaim, 'id'> = {
        warrantyId: data.warrantyId,
        claimNumber,
        status: 'pending' as ClaimStatus,
        priority: data.priority,
        description: data.description,
        reportedDate: now,
        expectedResolutionDate: data.expectedResolutionDate,
        assignedTo: data.assignedTo,
        organizationId,
        createdAt: now,
        updatedAt: now,
        createdBy: userId,
        notes: data.notes
      };

      const docRef = await addDoc(collection(db, CLAIMS_COLLECTION_NAME), {
        ...claimData,
        reportedDate: Timestamp.fromDate(claimData.reportedDate),
        expectedResolutionDate: claimData.expectedResolutionDate ? 
          Timestamp.fromDate(claimData.expectedResolutionDate) : null,
        createdAt: Timestamp.fromDate(claimData.createdAt),
        updatedAt: Timestamp.fromDate(claimData.updatedAt)
      });

      // Update warranty claims count
      const warrantyRef = doc(db, COLLECTION_NAME, data.warrantyId);
      await updateDoc(warrantyRef, {
        claimsCount: increment(1),
        status: 'claimed',
        updatedAt: Timestamp.fromDate(new Date())
      });

      return docRef.id;
    } catch (error) {
      console.error('Error creating warranty claim:', error);
      throw error;
    }
  }

  // Get warranty claims
  async getWarrantyClaims(organizationId: string, params: WarrantyClaimQueryParams = {}): Promise<WarrantyClaim[]> {
    try {
      let q = query(
        collection(db, CLAIMS_COLLECTION_NAME),
        where('organizationId', '==', organizationId),
        orderBy('createdAt', 'desc')
      );

      if (params.status) {
        q = query(q, where('status', '==', params.status));
      }

      if (params.priority) {
        q = query(q, where('priority', '==', params.priority));
      }

      if (params.warrantyId) {
        q = query(q, where('warrantyId', '==', params.warrantyId));
      }

      if (params.assignedTo) {
        q = query(q, where('assignedTo', '==', params.assignedTo));
      }

      const querySnapshot = await getDocs(q);
      let claims = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        reportedDate: doc.data().reportedDate?.toDate() || new Date(),
        expectedResolutionDate: doc.data().expectedResolutionDate?.toDate(),
        actualResolutionDate: doc.data().actualResolutionDate?.toDate(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as WarrantyClaim[];

      if (params.searchTerm) {
        const searchLower = params.searchTerm.toLowerCase();
        claims = claims.filter(c => 
          c.claimNumber.toLowerCase().includes(searchLower) ||
          c.description.toLowerCase().includes(searchLower)
        );
      }

      return claims;
    } catch (error) {
      console.error('Error getting warranty claims:', error);
      throw error;
    }
  }

  // Get warranty statistics
  async getWarrantyStats(organizationId: string): Promise<WarrantyStats> {
    try {
      const warranties = await this.getWarranties(organizationId);
      const claims = await this.getWarrantyClaims(organizationId);

      const now = new Date();
      const in30Days = new Date();
      in30Days.setDate(now.getDate() + 30);
      const in7Days = new Date();
      in7Days.setDate(now.getDate() + 7);

      const stats: WarrantyStats = {
        total: warranties.length,
        active: warranties.filter(w => w.status === 'active').length,
        expired: warranties.filter(w => w.status === 'expired').length,
        claimed: warranties.filter(w => w.status === 'claimed').length,
        expiringIn30Days: warranties.filter(w => 
          w.status === 'active' && w.endDate <= in30Days && w.endDate > now
        ).length,
        expiringIn7Days: warranties.filter(w => 
          w.status === 'active' && w.endDate <= in7Days && w.endDate > now
        ).length,
        claimsTotal: claims.length,
        claimsPending: claims.filter(c => c.status === 'pending').length,
        claimsResolved: claims.filter(c => c.status === 'completed').length,
        averageClaimResolutionDays: this.calculateAverageResolutionDays(claims)
      };

      return stats;
    } catch (error) {
      console.error('Error getting warranty stats:', error);
      throw error;
    }
  }

  private calculateAverageResolutionDays(claims: WarrantyClaim[]): number {
    const resolvedClaims = claims.filter(c => c.status === 'completed' && c.actualResolutionDate);
    
    if (resolvedClaims.length === 0) return 0;

    const totalDays = resolvedClaims.reduce((sum, claim) => {
      const diffTime = claim.actualResolutionDate!.getTime() - claim.reportedDate.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return sum + diffDays;
    }, 0);

    return Math.round(totalDays / resolvedClaims.length);
  }

  // Update expired warranties
  async updateExpiredWarranties(organizationId: string): Promise<void> {
    try {
      const warranties = await this.getWarranties(organizationId);
      const now = new Date();
      const batch = writeBatch(db);

      warranties.forEach(warranty => {
        if (warranty.status === 'active' && warranty.endDate < now) {
          const warrantyRef = doc(db, COLLECTION_NAME, warranty.id);
          batch.update(warrantyRef, {
            status: 'expired',
            updatedAt: Timestamp.fromDate(now)
          });
        }
      });

      await batch.commit();
    } catch (error) {
      console.error('Error updating expired warranties:', error);
      throw error;
    }
  }
}

export const warrantyService = new WarrantyService(); 